import PropTypes from "prop-types";
import React, { useContext, useEffect, useRef, useState } from "react";

import { classNames } from "@swa-ui/string";

import { AriaLive } from "../AriaLive";
import { ConfigurationContext } from "../ConfigurationContext";
import { TransitionBlock, transitionBlockPropTypes } from "../TransitionBlock";
import styles from "./List.module.scss";

const CASCADE_DELAY = 22.5;
const TRANSITION_IN = { duration: 400, transformations: ["fadeReset"] };
const TRANSITION_INITIAL = { duration: 400, transformations: ["fade"] };
const TRANSITION_OUT = { duration: 200, transformations: ["fade"] };
const UNOPENED = -1;

/**
 * General use component to display a list of items that can be swapped out with another list of items. The initial
 * render of the list will fade in if the animation prop is set to 'default'. Subsequent renders, when an updated list
 * is given, can be "slid" in from the left or right based on what's given for animation.
 */

export const List = React.memo((props) => {
  const {
    animate = true,
    animateInitialRender,
    animationToken,
    className,
    fullWidthContent = false,
    isGroup,
    itemClassName,
    items,
    listClassName,
    maxItemsToDisplay = 10,
    onTransformationEnd,
    startingIndex = 0,
    transformOrigin,
    transitionIn = TRANSITION_IN,
    transitionInitial,
    transitionOut = TRANSITION_OUT,
    wrapperComponent,
    wrapperComponentProps,
  } = props;
  const [currentAnimationToken, setCurrentAnimationToken] = useState(animationToken);
  const [currentItems, setCurrentItems] = useState(getItems());
  const [initialRender, setInitialRender] = useState(true);
  const configurationContext = useContext(ConfigurationContext);
  const [open, setOpen] = useState(configurationContext?.open || UNOPENED);
  const itemRefs = useRef([]);

  useEffect(() => {
    setInitialRender(false);
  }, [initialRender]);

  useEffect(() => {
    setCurrentItems(getItems());
  }, [items]);

  return (
    <ConfigurationContext.Provider value={getContextValue()}>
      {renderOptionalGroupContainer()}
    </ConfigurationContext.Provider>
  );

  function renderOptionalGroupContainer() {
    const outerProps = {
      "aria-label": props["aria-label"],
      "aria-labelledby": props["aria-labelledby"],
      className: getClass(),
    };
    const listItems = currentItems && renderList();

    return isGroup ? (
      <div role="group" {...outerProps}>
        <ul className={listClassName}>{listItems}</ul>
      </div>
    ) : (
      <ul {...outerProps}>{listItems}</ul>
    );
  }

  function renderList() {
    const content = [];
    const lastItemToDisplay = getMaxPageNumber() * maxItemsToDisplay;
    let index;

    for (index = startingIndex; index < lastItemToDisplay; index += 1) {
      content.push(renderListItem(currentItems[index], index));
    }

    return content;
  }

  function renderListItem(item, index) {
    return index >= startingIndex && index < startingIndex + maxItemsToDisplay ? (
      <li {...getListItemProps(index)}>{renderContent(item, index)}</li>
    ) : null;
  }

  function renderContent(item, index) {
    let content = (
      <AriaLive {...getAriaLiveProps()}>
        {animate ? (
          <TransitionBlock {...getTransitionBlockProps(index)}>{item}</TransitionBlock>
        ) : (
          item
        )}
      </AriaLive>
    );

    if (wrapperComponent) {
      const Component = wrapperComponent;

      content = <Component {...wrapperComponentProps}>{content}</Component>;
    }

    return content;
  }

  function getTransitionBlockProps(index) {
    const delay = { delay: (index - startingIndex) * CASCADE_DELAY };
    const lastItemToDisplay = Math.min(startingIndex + maxItemsToDisplay, currentItems.length);

    return {
      animationToken:
        initialRender && animateInitialRender
          ? "initial render"
          : `${currentAnimationToken}-${currentItems[index]?.key}`,
      className: classNames(styles.item, itemClassName),
      hideWhenOffScreen: false,
      key: index,
      onTransformationEnd: index === lastItemToDisplay - 1 ? handleTransformationEnd : undefined,
      transformOrigin,
      transitionIn: { ...transitionIn, ...delay },
      transitionInitial: getInitialTransition(),
      transitionOut: { ...transitionOut, ...delay },
    };
  }

  function getListItemProps(index) {
    const itemAriaLabel = items[index]?.["aria-label"];

    return {
      "aria-label": itemAriaLabel,
      className: classNames(styles.listItemContainer, { [styles.open]: open === index }),
      key: index - startingIndex,
      ref: (element) => (itemRefs.current[index] = element),
    };
  }

  function getAriaLiveProps() {
    return {
      className: styles.ariaLive,
      hiddenFromScreen: false,
    };
  }

  function getClass() {
    return classNames(className, {
      [styles.fullWidth]: fullWidthContent,
      [styles.listContainer]: open === UNOPENED,
    });
  }

  function handleTransformationEnd() {
    setCurrentAnimationToken(animationToken);
    onTransformationEnd?.();
  }

  function getContextValue() {
    return {
      ...configurationContext,
      ...{ handleCaption: [...(configurationContext?.handleCaption ?? []), handleCaption] },
    };
  }

  function getItems() {
    return items?.[0]?.item ? items.map(({ item }) => item) : items;
  }

  function handleCaption(captionInfo) {
    const { mainContent, open: opened } = captionInfo;

    setOpen(opened ? findParentListItemForTarget(mainContent) : UNOPENED);
  }

  function findParentListItemForTarget(target) {
    return itemRefs.current.findIndex((item) => item?.contains(target));
  }

  function getMaxPageNumber() {
    return (
      Math.floor(currentItems.length / maxItemsToDisplay) +
      (currentItems.length % maxItemsToDisplay ? 1 : 0)
    );
  }

  function getInitialTransition() {
    return transitionInitial
      ? transitionInitial
      : animateInitialRender
      ? TRANSITION_INITIAL
      : undefined;
  }
});

export const listPropTypes = {
  /** Determines if animations via TransitionBlock will be used. */
  animate: PropTypes.bool,

  /** Indicator that the list will be shown via transition when first rendered. The transition props will be used. */
  animateInitialRender: PropTypes.bool,

  /** Unique id that forces an animation (re-render). See TransitionBlock for more details. */
  animationToken: transitionBlockPropTypes.animationToken,

  /** String which provides an accessible name for the outermost element. */
  "aria-label": PropTypes.string,

  /** String which provides an accessible name via another element for the outermost element. */
  "aria-labelledby": PropTypes.string,

  /**
   * Additional classes applied to the outermost element: div if using the isGroup prop, ul otherwise. Given classes
   * may only position this component for layout purposes, and cannot change how the component renders in any way.
   */
  className: PropTypes.string,

  /** Determines if the content will spread to the entire width of its container. */
  fullWidthContent: PropTypes.bool,

  /** Determines if the list will be wrapped in a <div role="group">. */
  isGroup: PropTypes.bool,

  /** Class to be added to each item's container. */
  itemClassName: PropTypes.string,

  /**
   * Content List to be displayed. No styling will be defined for each item, and all styling should be provided by
   * content being passed in. List can also be an object with both the item node and its associated aria-label.
   */
  items: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.arrayOf(
      PropTypes.shape({
        "aria-label": PropTypes.string,
        item: PropTypes.node,
      })
    ),
  ]),

  /** Class applied to the ul element, only if using isGroup. Use className otherwise. */
  listClassName: PropTypes.string,

  /** Defines how items will be rendered. */
  maxItemsToDisplay: PropTypes.number,

  /** Callback to inform when a new set of content has been rendered. */
  onTransformationEnd: PropTypes.func,

  /** Index of first item to display. */
  startingIndex: PropTypes.number,

  /** Pertains to how things are rotated and scaled. See Transform for more info. */
  transformOrigin: transitionBlockPropTypes.transformOrigin,

  /** Array of transformations to use when a content change is animated in. */
  transitionIn: transitionBlockPropTypes.transitionIn,

  /** Animation options to apply to child content. See Transform for more info. */
  transitionInitial: transitionBlockPropTypes.transitionInitial,

  /** Array of transformations to use when a content change is animated out. */
  transitionOut: transitionBlockPropTypes.transitionOut,

  /**
   * Optional component to wrap content that will be transitioned when new content is given. The wrapper will not
   * transition which will provide a "less dynamic" looking transition.
   */
  wrapperComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),

  /** Props to be given to wrapperComponent. */
  wrapperComponentProps: PropTypes.object,
};

List.displayName = "List";

List.propTypes = listPropTypes;
